






















































































































































































































































import { Prop, Vue } from 'vue-property-decorator';
import Component from 'vue-class-component';

import { LineItemViewObject, StockInformationViewObject } from '@/types/apiServerContract';

import basketService from '@/project/basket/basket.service';
import stockFetchService from '@/project/product/services/stockFetch.service';
import authService from '@/core/auth/auth.service';
import detailedPriceFetchService, { PriceDetailedDataExtended } from '@/project/product/services/detailedPriceFetch.service';
import GTMService, { ProductTrackingState, TrackingLineItem } from '@/project/tracking/gtm/GTM.service';
import logService from '@/core/logging.service';

import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';

import ProductTileImage from '@/project/product/product-tile/ProductTileImage.vue';
import ProductTileStock from '@/project/product/product-tile/ProductTileStock.vue';
import AnimateValue from '@/core/ui-components/AnimateValue.vue';
import Temporary from '@/core/ui-components/Temporary.vue';
import InputText from '@/project/form/InputText.vue';
import CopyText from '@/project/shared/CopyText.vue';
import ProductListTracker from '@/project/tracking/gtm/ProductListTracker.vue';
import ReadMore from '@/core/ui-components/ReadMore.vue';
import permissionService from '@/project/auth/permission.service';
const BasketFavorite = () => import(/* webpackChunkName: 'basket-feature' */'@/project/app/client-pages/basket-page/BasketFavorite.vue');
const AddToBasket = () => import(/* webpackChunkName: 'auth-feature' */'@/project/basket/AddToBasket.vue');
const ProductTileCondition = () => import(/* webpackChunkName: 'auth-feature' */'@/project/product/product-tile/ProductTileCondition.vue');

@Component({
    components: {
        BasketFavorite,
        AddToBasket,
        ProductTileImage,
        ProductTileCondition,
        ProductTileStock,
        AnimateValue,
        Temporary,
        InputText,
        CopyText,
        ProductListTracker,
        ReadMore
    }
})
export default class BasketLineItem extends Vue {
    @Prop({
        required: false,
        type: Object
    }) model!: LineItemViewObject | null;

    @Prop({
        default: false,
        type: Boolean
    }) isFirstBlock!: boolean;

    $refs!: Vue['$refs'] & {
        referenceField: HTMLInputElement
    }

    private reference = this.model && this.model.reference ? this.model.reference : '';
    private stockData: readonly StockInformationViewObject[] | null = null;
    private stockLoaded: boolean = false;
    private showReferenceAnimation: boolean = false;
    private priceData: PriceDetailedDataExtended | null = null;
    private pricesLoaded: boolean = false;
    private isERPServiceAvailable = true;
    private meatballOverlayActive: boolean = false;

    private get referenceId() {
        return `ref-${this.model?.lineItemId}`;
    }

    private get referenceValue() {
        return this.reference;
    }

    get showFavorite(): boolean {
        return permissionService.canPunchout() === false;
    }

    private get productUrl() {
        return this.model?.url;
    }

    private get canRemoveLineItem() {
        return !this.model?.quoteId;
    }

    private get customer() {
        return myAccountStore.customer;
    }

    private isLineItemReferenceMandatory(wid: string, bidId: number | undefined | null, quoteId: string | undefined) {
        return !!this.customer?.lineReferenceRequired || basketService.isLineItemDuplicated(wid, bidId, quoteId);
    }

    private get supplementaryProducts() {
        return this.model?.supplementaryProducts ?? [];
    }

    private showMeatballOverlay() {
        this.meatballOverlayActive = true;
    }

    private closeMeatballOverlay() {
        this.meatballOverlayActive = false;
    }

    private removeLineItem(lineItemId: string) : void {
        if (this.canRemoveLineItem) {
            basketService.removeLineItem(lineItemId);
        }
    }

    private duplicateLineItem(lineItemId: string): void {
        basketService.duplicateLineItem(lineItemId);
        this.closeMeatballOverlay();
    }

    private lineItemClickFavorite(state:boolean): void {
        if (!state) {
            this.closeMeatballOverlay();
        }
    }

    private updateLineItem() : void {
        if (!this.model) return;

        // Only trigger animation if the input differ
        if (this.model?.reference !== this.$refs.referenceField.value) {
            basketService.updateLineItemReference(this.model.lineItemId, this.model.bidId, this.model.quoteId, this.reference);
            this.showReferenceAnimation = true;
        }
    }

    private resetAnim() {
        this.showReferenceAnimation = false;
    }

    private get hasBidTexts(): boolean {
        if (this.model && 'bidDescription' in this.model && 'bidNumber' in this.model) {
            return !!(this.model.bidDescription && this.model.bidNumber);
        }
        return false;
    }

    private get hasOriginalPrice(): boolean {
        return !!this.model && this.model.originalUnitPrice !== null && this.model.originalUnitPrice !== undefined;
    }

    private async getStockData() {
        try {
            const request = stockFetchService.requestData(this.model!.itemId);
            const result = await request.promise;
            this.stockData = Object.freeze(result.stockInformation);
            this.isERPServiceAvailable = this.isERPServiceAvailable && result.isServiceAvailable;
        } catch (error) {
            logService.error(error);
        } finally {
            this.stockLoaded = true;
            this.maybeIsComplete();
        }
    }

    private maybeIsComplete(): void {
        if (!this.pricesLoaded || !this.stockLoaded) return;

        this.$emit('trackingModelComplete', {
            ...this.model,
            stockInfo: this.stockData,
            trackingState: this.getTrackingState
        } as TrackingLineItem);
    }

    private async getPriceData() {
        if (!this.isUserLoggedIn) {
            this.pricesLoaded = true;
            this.maybeIsComplete();
            return;
        }

        try {
            const request = detailedPriceFetchService.requestData(this.model!.itemId);
            const result = await request.promise;

            this.priceData = Object.freeze(result);
            this.isERPServiceAvailable = this.isERPServiceAvailable && result.isServiceAvailable;
        } catch (error) {
            logService.error(error);
            this.isERPServiceAvailable = false;
        } finally {
            if (!this.isERPServiceAvailable) {
                this.$emit('priceNotAvailable');
            }
            this.pricesLoaded = true;
            this.maybeIsComplete();
        }
    }

    private get isUserLoggedIn(): boolean {
        return authService.isLoggedIn();
    }

    private trackQuantityChange(deltaQuantity: number) {
        GTMService.trackLineItemQuantityChange({
            ...this.model!,
            stockInfo: this.stockData,
            trackingState: this.getTrackingState
        }, deltaQuantity);
    }

    private rawProductClick() {
        GTMService.lineItemClick({
            ...this.model!,
            stockInfo: this.stockData,
            trackingState: this.getTrackingState
        }, 'basket');
    }

    private formatDutyDescription(dutyText) {
        return `(${dutyText.toLowerCase()})`;
    }

    created() {
        if (this.model) {
            this.getPriceData();
            this.getStockData();
        }
    }

    private get getTrackingState(): ProductTrackingState {
        if (!this.pricesLoaded || !this.stockLoaded) return ProductTrackingState.NoErpResponseYet;
        return this.isERPServiceAvailable ? ProductTrackingState.Complete : ProductTrackingState.ErpNotAvailable;
    }
}
